import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

function createKasirInvitationTemplates() {
  return {
    templates: [],
    categories: [],
    layouts: [],
    capabilities: {},
    loading: false,
    saving: false,
    modal: false,
    editing: false,
    form: {
      id: null,
      name: '',
      category_id: '',
      description: '',
      view_key: 'classic',
      is_premium: false,
      price: 0,
      is_featured: false,
      is_active: true,
    },

    init() {
      try {
        const cfg = JSON.parse(this.$el.getAttribute('data-config') || '{}');
        this.categories = cfg.categories || [];
        this.layouts = cfg.layouts || [];
        if (this.categories.length) this.form.category_id = this.categories[0].id;
      } catch (e) {
        console.error(e);
      }
      this.fetchList();
    },

    async fetchList() {
      this.loading = true;
      try {
        const { data } = await axios.get('/api/v1/kasir/engagement/invitation-templates');
        const payload = data.data || {};
        this.templates = payload.items || payload || [];
        this.capabilities = payload.capabilities || {};
        if (Array.isArray(payload)) this.templates = payload;
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat template.', 'error');
      } finally {
        this.loading = false;
      }
    },

    openCreate() {
      this.editing = false;
      this.form = {
        id: null,
        name: '',
        category_id: this.categories[0]?.id || '',
        description: '',
        view_key: 'classic',
        is_premium: false,
        price: 0,
        is_featured: false,
        is_active: false,
      };
      this.modal = true;
    },

    openEdit(t) {
      this.editing = true;
      this.form = {
        id: t.id,
        name: t.name || '',
        category_id: t.category?.id || this.categories[0]?.id || '',
        description: t.description || '',
        view_key: t.view_key || 'classic',
        is_premium: !!t.is_premium,
        price: t.price || 0,
        is_featured: !!t.is_featured,
        is_active: !!t.is_active,
      };
      this.modal = true;
    },

    async save() {
      this.saving = true;
      try {
        const body = { ...this.form };
        delete body.id;
        if (!this.capabilities.can_feature) delete body.is_featured;
        if (this.editing) {
          const { data } = await axios.put(`/api/v1/kasir/engagement/invitation-templates/${this.form.id}`, body);
          const idx = this.templates.findIndex((t) => t.id === data.data.id);
          if (idx >= 0) this.templates[idx] = data.data;
          window.toast?.(data.message || 'Diperbarui.', 'success');
        } else {
          const { data } = await axios.post('/api/v1/kasir/engagement/invitation-templates', body);
          this.templates.unshift(data.data);
          window.toast?.(data.message || 'Ditambahkan.', 'success');
        }
        this.modal = false;
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menyimpan.', 'error');
      } finally {
        this.saving = false;
      }
    },

    async cloneTemplate(t) {
      const name = window.prompt('Nama template clone (opsional):', `${t.name} (Copy)`);
      if (name === null) return;
      try {
        const { data } = await axios.post(`/api/v1/kasir/engagement/invitation-templates/${t.id}/clone`, { name: name || undefined });
        this.templates.unshift(data.data);
        window.toast?.(data.message || 'Template di-clone.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal clone.', 'error');
      }
    },

    async submitTemplate(t) {
      try {
        const { data } = await axios.post(`/api/v1/kasir/engagement/invitation-templates/${t.id}/submit`);
        this.patchTemplate(data.data);
        window.toast?.(data.message || 'Diajukan untuk review.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal submit.', 'error');
      }
    },

    async approve(t) {
      try {
        const { data } = await axios.post(`/api/v1/kasir/engagement/invitation-templates/${t.id}/approve`);
        this.patchTemplate(data.data);
        window.toast?.(data.message || 'Disetujui.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal approve.', 'error');
      }
    },

    async reject(t) {
      const reason = window.prompt('Alasan penolakan:');
      if (!reason) return;
      try {
        const { data } = await axios.post(`/api/v1/kasir/engagement/invitation-templates/${t.id}/reject`, { reason });
        this.patchTemplate(data.data);
        window.toast?.(data.message || 'Ditolak.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal reject.', 'error');
      }
    },

    async unpublish(t) {
      const ok = await window.confirmDialog?.({
        title: 'Unpublish template?',
        message: `Template "${t.name}" tidak akan tampil publik.`,
        confirmText: 'Unpublish',
        variant: 'danger',
      });
      if (!ok) return;
      try {
        const { data } = await axios.post(`/api/v1/kasir/engagement/invitation-templates/${t.id}/unpublish`);
        this.patchTemplate(data.data);
        window.toast?.(data.message || 'Di-unpublish.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal unpublish.', 'error');
      }
    },

    async toggleFeature(t) {
      try {
        const { data } = await axios.post(`/api/v1/kasir/engagement/invitation-templates/${t.id}/feature`, {
          is_featured: !t.is_featured,
        });
        this.patchTemplate(data.data);
        window.toast?.(data.message || 'Featured diperbarui.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal update featured.', 'error');
      }
    },

    async hold(t) {
      const reason = window.prompt('Alasan hold fee/status (opsional):') || '';
      try {
        const { data } = await axios.post(`/api/v1/kasir/engagement/invitation-templates/${t.id}/hold`, { reason });
        this.patchTemplate(data.data);
        window.toast?.(data.message || 'Di-hold.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal hold.', 'error');
      }
    },

    async remove(t) {
      const ok = await window.confirmDialog?.({
        title: 'Hapus template?',
        message: `Hapus ${t.name}?`,
        confirmText: 'Hapus',
        variant: 'danger',
      });
      if (!ok) return;
      try {
        await axios.delete(`/api/v1/kasir/engagement/invitation-templates/${t.id}`);
        this.templates = this.templates.filter((x) => x.id !== t.id);
        window.toast?.('Template dihapus.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal hapus.', 'error');
      }
    },

    patchTemplate(row) {
      const idx = this.templates.findIndex((x) => x.id === row.id);
      if (idx >= 0) this.templates[idx] = row;
    },
  };
}

Alpine.data('kasirInvitationTemplates', createKasirInvitationTemplates);
